import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { API_BASE_URL } from "../config/apiConfig";
import { isPublicRoute } from "../utils/publicRoutes";

const HEARTBEAT_INTERVAL_MS = 60 * 1000;

// Replaces the old login-to-logout wall-clock session tracking (which
// produced wildly inflated "study time" totals — see
// Teechaa-backend/utils/studyStats.js for the full history). Instead, while
// the user is on an authenticated page AND the tab is actually visible, this
// pings the backend roughly once a minute with the real elapsed seconds
// since the last ping, so total study time only grows while someone is
// actually looking at the app.
const sendHeartbeat = (activeSeconds) => {
    const token = localStorage.getItem("token");
    if (!token || activeSeconds <= 0) return;

    fetch(`${API_BASE_URL}/api/users/heartbeat`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ activeSeconds }),
    }).catch((error) => {
        console.error("Heartbeat failed:", error);
    });
};

export const useStudyHeartbeat = () => {
    const location = useLocation();
    const lastTickRef = useRef(Date.now());

    useEffect(() => {
        if (isPublicRoute(location.pathname)) return undefined;

        lastTickRef.current = Date.now();

        const tick = () => {
            if (document.visibilityState !== "visible") return;
            const now = Date.now();
            const elapsedSeconds = Math.round((now - lastTickRef.current) / 1000);
            lastTickRef.current = now;
            sendHeartbeat(elapsedSeconds);
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                // Resuming from hidden — don't count the time it was hidden.
                lastTickRef.current = Date.now();
            }
        };

        const interval = setInterval(tick, HEARTBEAT_INTERVAL_MS);
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            clearInterval(interval);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [location.pathname]);
};
